import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api";
import { toast } from "sonner";
import { X, UserPlus, Search } from "lucide-react";

// Collaborazioni cliniche: inviti ricevuti/concessi + invito (@username o email). Un solo ruolo "Collaboratore clinico".
export function Collaborations({ onClose, onChanged }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState("received");
  const [data, setData] = useState({ received: [], granted: [] });
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/collaborations").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (selected || q.trim().length < 1) { setResults([]); return; }
    const id = setTimeout(() => api.get("/collaborations/search", { params: { q: q.replace(/^@/, "") } }).then(({ data }) => setResults(data || [])).catch(() => {}), 250);
    return () => clearTimeout(id);
  }, [q, selected]);

  const act = async (fn) => { setBusy(true); try { await fn(); load(); onChanged && onChanged(); } finally { setBusy(false); } };
  // 1° clic: SELEZIONA soltanto (nessun invio/email/notifica)
  const pick = (p) => { setSelected(p); setQ(""); setResults([]); };
  const clearSel = () => { setSelected(null); setQ(""); setResults([]); };
  // 2° clic: conferma esplicita → crea l'invito
  const sendSelected = () => {
    if (!selected || busy) return;
    act(async () => {
      const { data } = await api.post("/collaborations/invite", { collaborator_id: selected.id });
      if (data.already && data.status === "active") toast.message(t("diag.co_already_active"));
      else if (data.already && data.status === "pending") toast.message(t("diag.co_already_pending"));
      else toast.success(t("diag.co_sent"));
      setSelected(null);
    });
  };
  const inviteEmail = () => {
    if (!email.trim() || !email.includes("@")) { toast.message(t("diag.co_email_ph")); return; }
    act(async () => { await api.post("/collaborations/invite", { email }); toast.success(t("diag.co_sent")); setEmail(""); });
  };
  const accept = (id) => act(async () => { await api.post(`/collaborations/${id}/accept`); toast.success(t("diag.co_active")); });
  const decline = (id) => act(async () => { await api.post(`/collaborations/${id}/decline`); });
  const revoke = (id) => act(async () => { await api.post(`/collaborations/${id}/revoke`); });

  const tabCls = (a) => `flex-1 text-sm font-medium px-3 py-2 rounded-lg transition-colors ${a ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`;
  const badge = (s) => <span className={`text-xs px-2 py-0.5 rounded-full ${s === "active" ? "bg-emerald-100 text-emerald-700" : s === "pending" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-500"}`}>{t(s === "active" ? "diag.co_active" : s === "pending" ? "diag.co_pending" : "diag.co_declined")}</span>;

  return (<div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="collab-overlay">
    <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="collab-modal">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-lg text-slate-900">{t("diag.co_collabs")}</h3>
        <button className="text-slate-400 hover:text-slate-700" onClick={onClose} data-testid="collab-close"><X size={18} /></button>
      </div>

      {/* Invito */}
      <div className="rounded-xl border border-slate-200 p-3 mb-4">
        <div className="text-sm font-semibold mb-2 flex items-center gap-1.5"><UserPlus size={15} />{t("diag.co_invite")}</div>
        {!selected ? (
          <div className="relative mb-2">
            <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
            <input className="yp-input !py-1.5 text-sm pl-8 w-full" autoComplete="off" placeholder={t("diag.co_search_ph")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="collab-search" />
            {results.length > 0 && <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow max-h-56 overflow-y-auto">
              {results.map((p) => (
                <button key={p.id} className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex items-center gap-2" onClick={() => pick(p)} data-testid="collab-search-result">
                  {p.avatar_url ? <img src={p.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" /> : <span className="w-7 h-7 rounded-full bg-slate-100 shrink-0" />}
                  <span className="min-w-0">
                    <span className="block font-medium truncate">{p.title ? `${p.title} ` : ""}{p.name}</span>
                    <span className="block text-xs text-slate-500 truncate">{p.specialization || ""}{p.username ? `${p.specialization ? " · " : ""}@${p.username}` : ""}</span>
                  </span>
                </button>))}
            </div>}
          </div>
        ) : (
          <div className="mb-2 rounded-lg border border-blue-200 bg-blue-50/60 p-2.5" data-testid="collab-selected">
            <div className="text-[11px] font-semibold text-blue-700 uppercase tracking-wide mb-1.5">{t("diag.co_recipient")}</div>
            <div className="flex items-center gap-2">
              {selected.avatar_url ? <img src={selected.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" /> : <span className="w-9 h-9 rounded-full bg-white border border-slate-200 shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-900 truncate" data-testid="collab-selected-name">{selected.title ? `${selected.title} ` : ""}{selected.name}</div>
                <div className="text-xs text-slate-600 truncate">{selected.specialization || ""}{selected.username ? `${selected.specialization ? " · " : ""}@${selected.username}` : ""}</div>
              </div>
              <button className="text-xs text-slate-500 hover:text-slate-800 shrink-0" disabled={busy} onClick={clearSel} data-testid="collab-change">{t("diag.co_change")}</button>
            </div>
            <button className="yp-btn yp-btn-primary !py-1.5 w-full mt-2.5 text-sm" disabled={busy} onClick={sendSelected} data-testid="collab-send">{t("diag.co_send")}</button>
          </div>
        )}
        <div className="flex gap-2">
          <input className="yp-input !py-1.5 text-sm flex-1" type="email" autoComplete="off" placeholder={t("diag.co_email_ph")} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="collab-email" />
          <button className="yp-btn yp-btn-primary !py-1.5 !px-3 text-sm" disabled={busy} onClick={inviteEmail} data-testid="collab-email-send">{t("diag.co_send")}</button>
        </div>
      </div>

      <div className="flex gap-2 mb-3">
        <button className={tabCls(tab === "received")} onClick={() => setTab("received")} data-testid="collab-tab-received">{t("diag.co_received")}</button>
        <button className={tabCls(tab === "granted")} onClick={() => setTab("granted")} data-testid="collab-tab-granted">{t("diag.co_granted")}</button>
      </div>

      {tab === "received" && <div className="space-y-2" data-testid="collab-received-list">
        {data.received.length === 0 && <div className="text-sm text-slate-400">{t("diag.co_none")}</div>}
        {data.received.map((c) => (
          <div key={c.id} className="flex items-center justify-between gap-2 border-b border-slate-100 pb-2" data-testid="collab-received-row">
            <div><div className="text-sm font-medium">{c.owner_name}</div>{badge(c.status)}</div>
            {c.status === "pending" && <div className="flex gap-2">
              <button className="yp-btn yp-btn-primary !py-1 !px-3 text-xs" disabled={busy} onClick={() => accept(c.id)} data-testid="collab-accept">{t("diag.co_accept")}</button>
              <button className="yp-btn yp-btn-ghost !py-1 !px-3 text-xs" disabled={busy} onClick={() => decline(c.id)} data-testid="collab-decline">{t("diag.co_decline")}</button>
            </div>}
          </div>))}
      </div>}

      {tab === "granted" && <div className="space-y-2" data-testid="collab-granted-list">
        {data.granted.length === 0 && <div className="text-sm text-slate-400">{t("diag.co_none")}</div>}
        {data.granted.map((c) => (
          <div key={c.id} className="flex items-center justify-between gap-2 border-b border-slate-100 pb-2" data-testid="collab-granted-row">
            <div><div className="text-sm font-medium">{c.collaborator_name}</div>{badge(c.status)}</div>
            <button className="yp-btn yp-btn-ghost !py-1 !px-3 text-xs text-rose-600" disabled={busy} onClick={() => revoke(c.id)} data-testid="collab-revoke">{t("diag.co_revoke")}</button>
          </div>))}
      </div>}
    </div>
  </div>);
}
